import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { DatastoreCollection } from '@freelancer/datastore';
import { Task, TasksCollection } from '@freelancer/datastore/collections';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { trackById } from '@freelancer/ui/helpers';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { ModalSize } from '@freelancer/ui/modal';
import {
  FontColor,
  FontStyle,
  FontWeight,
  TextSize,
} from '@freelancer/ui/text';
import { TaskStatusApi } from 'api-typings/tasklist/tasklist';
import { createError } from '../task-error/task-error.helper';
import {
  TaskError,
  TaskErrorAction,
  TaskErrorRemedy,
} from '../task-error/task-error.model';

@Component({
  selector: 'app-task-list',
  template: `
    <fl-bit
      class="ListContainer WebappCompatTasklist-container"
      [flMarginBottom]="Margin.MID"
    >
      <fl-bit class="ListHeader">
        <fl-heading [size]="TextSize.MID" [headingType]="HeadingType.H4">
          <ng-container
            *ngIf="listTitle === 'in_progress'"
            i18n="Tasklist list heading"
          >
            To-do
          </ng-container>
          <ng-container
            *ngIf="listTitle === 'closed_resolved'"
            i18n="Tasklist list heading"
          >
            Completed
          </ng-container>
        </fl-heading>
        <fl-button
          *ngIf="showCreateBtn"
          i18n="Tasklist new task button"
          [disabled]="disableCreateBtn"
          [color]="ButtonColor.SECONDARY"
          [size]="ButtonSize.SMALL"
          (click)="createNewTaskHandler()"
          flTrackingLabel="TaskCreateNew"
        >
          Create new task
        </fl-button>
      </fl-bit>
      <fl-bit>
        <fl-bit
          *ngIf="
            groupedTaskListCollection &&
              (groupedTaskListCollection.valueChanges()
                | flAsync) as groupedTaskList;
            else loadingTasklist
          "
        >
          <app-task-item
            *ngFor="let task of groupedTaskList; trackBy: trackById"
            [editingTaskId]="editingTaskId"
            [task]="task"
            (selected)="itemSelectedHandler($event)"
            (itemToggled)="itemToggledHandler($event)"
          ></app-task-item>
          <fl-bit class="EmptyArea" *ngIf="groupedTaskList?.length === 0">
            <fl-bit
              class="ClickArea"
              *ngIf="showCreateBtn"
              [flHideMobile]="true"
              flTrackingLabel="TaskListEmptyCreateNewTask"
              i18n="No in_progress tasks message"
              (click)="createNewTaskHandler()"
            >
              Click to create new task
            </fl-bit>
            <fl-bit *ngIf="!showCreateBtn">
              <fl-text
                [color]="FontColor.MID"
                [style]="FontStyle.ITALIC"
                i18n="No completed tasks message"
              >
                You don't have any completed tasks yet.
              </fl-text>
            </fl-bit>
          </fl-bit>
        </fl-bit>
        <ng-template #loadingTasklist>
          <fl-loading-text
            [rows]="4"
            [padded]="padding"
            ngClass="{ FreelancerPaddinng: !padding }"
          ></fl-loading-text>
        </ng-template>
      </fl-bit>
    </fl-bit>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./task-list.component.scss'],
})
export class TaskListComponent {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  TextSize = TextSize;
  FontStyle = FontStyle;
  FontWeight = FontWeight;
  HeadingType = HeadingType;
  IconColor = IconColor;
  IconSize = IconSize;
  Margin = Margin;
  ModalSize = ModalSize;
  trackById = trackById;

  @Input() disableCreateBtn: boolean;
  @Input() editingTaskId: number | undefined;
  @Input() groupedTaskListCollection: DatastoreCollection<TasksCollection>;
  @Input() listTitle: TaskStatusApi;
  @Input() showCreateBtn = false;
  @Input() padding = true;

  @Output() newTask = new EventEmitter<void>();
  @Output() selected = new EventEmitter<Task>();
  @Output() toggled = new EventEmitter<void>();
  @Output() error = new EventEmitter<TaskError>();
  @Output() hideError = new EventEmitter<void>();

  // Called when an item is selected
  itemSelectedHandler(selected: Task) {
    this.selected.emit(selected);
  }

  // Called when a list item state is toggled
  itemToggledHandler(selected: Task) {
    const status =
      selected.status === TaskStatusApi.IN_PROGRESS
        ? TaskStatusApi.CLOSED_RESOLVED
        : TaskStatusApi.IN_PROGRESS;
    this.groupedTaskListCollection.update(selected.id, { status }).then(res => {
      if (res.status === 'success') {
        this.hideError.emit();
      } else {
        this.error.emit(
          createError(
            TaskErrorAction.TASK_TOGGLE,
            TaskErrorRemedy.AGAIN,
            res.errorCode,
          ),
        );
      }
    });
  }

  // Called when the 'create new task' button is clicked
  createNewTaskHandler() {
    this.newTask.emit();
  }
}
